import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import ValidacaoService from "src/services/motor-validacao-service"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"

import {
    buildValidationPayload,
    createInitialValidationParameters,
    mapSavedValidation,
} from "../validation-contract"

const useValidationData = ({
    cadastroId,
    motorService,
    onEmissionSaved,
}) => {
    const validacaoService = useMemo(() => new ValidacaoService(), [])

    const [parameters, setParameters] = useState(
        createInitialValidationParameters
    )
    const [lookup, setLookup] = useState({
        key: null,
        loading: false,
        viewModel: null,
    })
    const [isSavingEmission, setIsSavingEmission] = useState(false)

    const mountedRef = useRef(false)
    const requestVersionRef = useRef(0)
    const savingEmissionRef = useRef(false)

    const [isSavingValidation, setIsSavingValidation] = useState(false)
    const savingValidationRef = useRef(false)

    const hasCadastroId = cadastroId !== null
        && cadastroId !== undefined
        && cadastroId !== ""

    const dataReferencia = parameters.dataReferencia
    const requestKey = JSON.stringify([cadastroId, dataReferencia])

    const currentLookup = lookup.key === requestKey
        ? lookup
        : {
            loading: hasCadastroId && Boolean(dataReferencia),
            viewModel: null,
        }

    useEffect(() => {
        mountedRef.current = true

        return () => {
            mountedRef.current = false
        }
    }, [])

    useEffect(() => {
        const requestVersion = ++requestVersionRef.current

        const isCurrent = () => (
            mountedRef.current
            && requestVersionRef.current === requestVersion
        )

        const cleanup = () => {
            if (requestVersionRef.current === requestVersion) {
                requestVersionRef.current += 1
            }
        }

        // Não mantém parâmetros e resultados da consulta anterior.
        setParameters((current) => ({
            ...current,
            puparReferencia: "",
            pumtmReferencia: "",
            spreadCredito: "",
        }))

        setLookup({
            key: requestKey,
            loading: hasCadastroId && Boolean(dataReferencia),
            viewModel: null,
        })

        if (!hasCadastroId || !dataReferencia) {
            return cleanup
        }

        const loadValidation = async () => {
            try {
                const response = await validacaoService.getUltimaValidacao(
                    cadastroId,
                    dataReferencia
                )

                // O ServiceRequest pode retornar undefined após redirecionar.
                if (!isCurrent() || !response) return

                const data = response.body

                // Response(None) também pode chegar como corpo vazio.
                if (!data || Object.keys(data).length === 0) return

                const mapped = mapSavedValidation(data)

                setParameters((current) => ({
                    ...current,
                    ...mapped.parameters,
                }))

                setLookup({
                    key: requestKey,
                    loading: false,
                    viewModel: mapped.viewModel,
                })
            } catch (error) {
                if (!isCurrent()) return

                dispatchSnackbar(
                    error.response?.body?.Message
                    || "Não foi possível carregar a validação salva.",
                    "error"
                )
            } finally {
                if (isCurrent()) {
                    setLookup((current) => ({
                        ...current,
                        loading: false,
                    }))
                }
            }
        }

        loadValidation()

        return cleanup
    }, [
        cadastroId,
        dataReferencia,
        hasCadastroId,
        requestKey,
        validacaoService,
    ])

    const saveEmission = useCallback(async ({
        spreadEmissao,
        prazoMedioEmissao,
    }) => {
        if (!hasCadastroId || savingEmissionRef.current) return

        savingEmissionRef.current = true
        setIsSavingEmission(true)

        const requestVersion = requestVersionRef.current

        const isCurrent = () => (
            mountedRef.current
            && requestVersionRef.current === requestVersion
        )

        try {
            const response = await motorService.updateCadastroCaracteristicas(
                cadastroId,
                {
                    SpreadEmissao: spreadEmissao,
                    PrazoMedioEmissao: prazoMedioEmissao,
                }
            )

            if (!isCurrent() || !response) return

            dispatchSnackbar(
                "Spread e prazo da emissão atualizados com sucesso.",
                "success"
            )

            // Recarrega as características utilizadas pelas demais abas.
            onEmissionSaved?.(cadastroId, isCurrent)

            return response.body
        } catch (error) {
            if (!isCurrent()) return

            dispatchSnackbar(
                error.response?.body?.Message
                || "Não foi possível atualizar o spread e o prazo da emissão.",
                "error"
            )
        } finally {
            savingEmissionRef.current = false

            if (mountedRef.current) {
                setIsSavingEmission(false)
            }
        }
    }, [
        cadastroId,
        hasCadastroId,
        motorService,
        onEmissionSaved,
    ])

    const saveValidation = useCallback(async (validationPayload) => {
        if (!hasCadastroId || savingValidationRef.current) return

        savingValidationRef.current = true
        setIsSavingValidation(true)

        const requestVersion = requestVersionRef.current

        const isCurrent = () => (
            mountedRef.current
            && requestVersionRef.current === requestVersion
        )

        try {
            const payload = buildValidationPayload(validationPayload)

            const response = await validacaoService.postValidacao(
                cadastroId,
                payload
            )

            if (!isCurrent() || !response) return

            dispatchSnackbar(
                "Validação salva com sucesso.",
                "success"
            )

            return response.body
        } catch (error) {
            if (!isCurrent()) return

            dispatchSnackbar(
                error.response?.body?.Message
                || "Não foi possível salvar a validação.",
                "error"
            )
        } finally {
            savingValidationRef.current = false

            if (mountedRef.current) {
                setIsSavingValidation(false)
            }
        }
    }, [
        cadastroId,
        hasCadastroId,
        validacaoService,
    ])

    const exportExcel = useCallback(async (payload, memoryColumns) => {
        return validacaoService.exportValidationExcel(payload, memoryColumns)
    }, [validacaoService])

    return {
        parameters,
        setParameters,
        savedViewModel: currentLookup.viewModel,
        isLoadingValidation: currentLookup.loading,
        isSavingEmission,
        saveEmission,
        isSavingValidation,
        saveValidation,
        exportExcel,
    }
}

export default useValidationData